import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useCallback, useMemo } from 'react';

import { Box, Button, Flex, HStack, Separator, Spinner, Text, VStack } from '@chakra-ui/react';

import debounce from 'lodash/debounce';
import { v4 as uuid } from 'uuid';

import { usePluralisationTranslation } from '@nocowanie/common-ui';

import { GuestsNightsLabel } from '@app/components/atoms/guests-nights-label';
import { GuestsPictograms } from '@app/components/atoms/guests-pictograms';
import { Price } from '@app/components/molecules/price';
import { ProvisionGuaranteeInfo } from '@app/components/molecules/provision-guarantee-info';
import { AppModalsEnum } from '@app/enums';
import { useDrawerState } from '@app/hooks';
import { apiTypes } from '@app/models';
import { accommodationSlice, commonUiStateSlice, useAppDispatch, useAppSelector } from '@app/state';

import { ApartmentVariantProps, ApartmentVariantsProps } from './apartment-variants.props';

const ApartmentVariant = ({
    apartmentId,
    variant,
    variantsCount,
    index,
}: ApartmentVariantProps) => {
    const { t } = useTranslation('accommodation');
    const pluralizeTranslation = usePluralisationTranslation('accommodation', 'guestsNightsLabel');
    const dispatch = useAppDispatch();
    const { push } = useRouter();

    const accommodation = useAppSelector(accommodationSlice.selectors.selectCurrentAccommodation);
    const variantUrl = useAppSelector(
        accommodationSlice.selectors.selectVariantUrl(variant, apartmentId),
    );

    const totalAdults = pluralizeTranslation(variant.guests, 'adult');
    const totalChildren = variant.childrenAges.length
        ? ` + ${pluralizeTranslation(variant.childrenAges.length, 'child')}`
        : null;

    const handleBtnClick = useCallback(() => {
        if (!variantUrl) return;

        push(variantUrl);
    }, [variantUrl]);

    const handleShowReservationDetails = useCallback(() => {
        dispatch(
            commonUiStateSlice.actions.setModalDataAction({
                modalName: AppModalsEnum.CancellationDetails,
                modalData: {
                    apartmentId,
                    variant,
                },
            }),
        );
    }, [apartmentId, variant]);

    const { isDrawerPending, openDrawer } = useDrawerState(
        debounce(() => handleShowReservationDetails(), 100),
    );

    return (
        <>
            <Flex
                width={'100%'}
                justify={'space-between'}
                align={{ base: 'flex-end', lg: 'flex-start' }}
                direction={{ base: 'column', lg: 'row' }}
                gap={4}
                data-testid="apartment-variant"
            >
                <HStack gap={4} align={'flex-start'} width={{ base: '100%', lg: undefined }}>
                    <Box flex={2} minW={140}>
                        <Text as={'b'} whiteSpace={'nowrap'} fontSize={'xs'}>
                            {totalAdults} {totalChildren}
                        </Text>
                        <GuestsPictograms
                            adultsCount={variant.guests}
                            childrenCount={variant.childrenAges.length}
                        />
                    </Box>
                    <Box flex={5} textAlign={{ base: 'right', lg: 'left' }}>
                        <ProvisionGuaranteeInfo
                            provision={variant.provision}
                            cancellationRules={variant.cancellationRules}
                            price={variant.convertedTotalPrice as number}
                            isWrapped
                            textAlign={{ base: 'right', lg: 'left' }}
                        />
                        <Button
                            justifyContent="flex-start"
                            variant="link"
                            colorPalette="secondary"
                            size={'sm'}
                            onClick={openDrawer}
                            data-testid="reservation-details-button"
                            mt={2}
                        >
                            {t('cancellation.reservationDetailsButton')}
                            {isDrawerPending ? <Spinner ml={2} size={'xs'} /> : null}
                        </Button>
                    </Box>
                </HStack>
                <Box flex={5} textAlign={'right'}>
                    <Price
                        currency={accommodation?.convertedRatesCurrency ?? ''}
                        price={variant.convertedTotalPrice as number}
                        basePrice={variant.convertedTotalBasePrice as number}
                        discounts={variant.discounts as apiTypes.Discount[]}
                    >
                        <GuestsNightsLabel
                            adultsCount={variant.guests}
                            childrenCount={variant.childrenAges.length}
                        />
                    </Price>
                    <Button
                        onClick={handleBtnClick}
                        variant={'outline'}
                        minW={220}
                        mt={1}
                        data-testid="variant-reserve-button"
                    >
                        {t('buttons.book')}
                    </Button>
                </Box>
            </Flex>
            {index < variantsCount - 1 ? <Separator mb={2} /> : null}
        </>
    );
};

export const ApartmentVariants = ({ apartmentId, ...props }: ApartmentVariantsProps) => {
    const variants = useAppSelector(
        accommodationSlice.selectors.selectVariantsForApartment(apartmentId),
    );

    const variantsWithIds = useMemo(() => {
        return variants?.map(variant => {
            return {
                ...variant,
                id: uuid(),
            };
        });
    }, [variants]);

    return (
        <VStack {...props}>
            {variants?.length
                ? variantsWithIds?.map((variant, i) => (
                      <ApartmentVariant
                          apartmentId={apartmentId}
                          variant={variant as apiTypes.Variant}
                          variantsCount={variants.length}
                          index={i}
                          key={variant.id}
                      />
                  ))
                : null}
        </VStack>
    );
};
